/**
 * Wall-crush FX — the impact when a half-screen crusher (family `walls`) SLAMS
 * shut. *Core decides, Phaser renders*: core owns the wall geometry + the moment it
 * goes lethal (telegraph→active); this paints the close so it lands with weight.
 *
 * On the close it fires, along the wall's inner "crush seam" (the edge facing the
 * play area):
 *   1. a white-hot FLASH strip down the seam that snaps then fades,
 *   2. a spray of steel DEBRIS chunks flung out toward the safe side (with a little
 *      gravity so they arc), and
 *   3. a short, low-amplitude CAMERA SHAKE for the slam.
 *
 * Decorative + pooled (one shared emitter, one reused flash image). Edge-triggered
 * once per wall by the director, so live play and replays slam identically.
 */
import Phaser from 'phaser';
import { HAZARD_TEX } from './textures';

const FX_DEPTH = 9; // debris: above hazards (5) + their FX, below the player (10)
/** The crush flash washes OVER the ball, so seat it clearly above the player (10/11)
 *  — a fixed depth (not the player's 10) keeps the draw order deterministic. */
const FLASH_DEPTH = 12;
const FLASH_MS = 220;
/** Emit points down the seam + chunks per point. */
const SEAM_POINTS = 16;
const CHUNKS_PER_POINT = 3;

export class WallFx {
  private readonly scene: Phaser.Scene;
  private emitter?: Phaser.GameObjects.Particles.ParticleEmitter;
  private flash?: Phaser.GameObjects.Image;
  private flashTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Slam FX along the crusher's inner seam. `vertical` seams (left/right walls) run
   * down `x = seamX` over `0..span` (span = height); horizontal seams (top/bottom
   * walls) run across `y = seamY` over `0..span` (span = width). Debris is flung at
   * `flingDeg` (0 = right, 180 = left, 90 = down, 270 = up — toward the open side).
   */
  crush(seamX: number, seamY: number, vertical: boolean, span: number, flingDeg: number): void {
    this.ensureObjects();

    // 1. Flash strip down the seam (white-hot, snaps then fades).
    const flash = this.flash!;
    this.flashTween?.stop();
    const fx = vertical ? seamX : span / 2;
    const fy = vertical ? span / 2 : seamY;
    const setThickness = (thick: number): void => {
      if (vertical) flash.setDisplaySize(thick, span);
      else flash.setDisplaySize(span, thick);
    };
    flash.setPosition(fx, fy).setActive(true).setVisible(true).setAlpha(0.95).setTint(0xffffff);
    setThickness(26);
    this.flashTween = this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: FLASH_MS,
      ease: 'quad.out',
      onUpdate: (tw) => setThickness(26 * (1 - tw.progress * 0.7)),
      onComplete: () => flash.setVisible(false).setActive(false),
    });

    // 2. Debris flung out along the seam (gravity arcs the chunks).
    const e = this.emitter!;
    e.updateConfig({ speed: { min: 90, max: 320 }, angle: { min: flingDeg - 42, max: flingDeg + 42 } });
    for (let i = 0; i < SEAM_POINTS; i++) {
      const t = (i / (SEAM_POINTS - 1)) * span;
      const ex = vertical ? seamX : t;
      const ey = vertical ? t : seamY;
      e.emitParticleAt(ex, ey, CHUNKS_PER_POINT);
    }

    // 3. A short slam shake.
    this.scene.cameras.main.shake(130, 0.006);
  }

  reset(): void {
    this.flashTween?.stop();
    this.flashTween = undefined;
    this.flash?.setVisible(false).setActive(false);
    this.emitter?.killAll();
  }

  destroy(): void {
    this.flashTween?.stop();
    this.emitter?.destroy();
    this.flash?.destroy();
    this.emitter = undefined;
    this.flash = undefined;
  }

  private ensureObjects(): void {
    if (!this.emitter) {
      this.emitter = this.scene.add
        .particles(0, 0, HAZARD_TEX.spark, {
          frequency: -1,
          lifespan: { min: 360, max: 760 },
          speed: { min: 90, max: 320 },
          angle: { min: -42, max: 42 },
          gravityY: 520, // chunks arc + fall
          scale: { start: 0.55, end: 0, ease: 'quad.out' },
          alpha: { start: 1, end: 0, ease: 'quad.in' },
          rotate: { min: 0, max: 360 },
          // Light steel → steel → dark, like shattered plate + dust.
          color: [0xc6d3e0, 0x7d94a8, 0x33485c],
          colorEase: 'quad.out',
          emitting: false,
        })
        .setDepth(FX_DEPTH);
      this.emitter.reserve(SEAM_POINTS * CHUNKS_PER_POINT * 2);
    }
    if (!this.flash) {
      this.flash = this.scene.add
        .image(0, 0, HAZARD_TEX.beam)
        .setDepth(FLASH_DEPTH)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setVisible(false)
        .setActive(false);
    }
  }
}
